import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Image from '@tiptap/extension-image';
import CharacterCount from '@tiptap/extension-character-count';
import { useEffect, useCallback } from 'react';
import { EditorToolbar } from './rich-text/EditorToolbar';
import { cleanupBlueHighlights, autoLinkContent } from './rich-text/editorUtils';

export default function RichTextEditor({ value, onChange, placeholder = "Text eingeben...", showCharacterCount = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // We use custom code block
      }),
      Underline,
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          class: 'text-primary underline hover:text-primary/80 cursor-pointer',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Highlight.configure({
        multicolor: true,
      }),
      TextStyle,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      CharacterCount,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[150px] p-4',
      },
    },
  });

  // Cleanup blue highlights function
  const handleCleanupHighlights = useCallback(() => {
    if (editor) {
      const currentHtml = editor.getHTML();
      const cleanedHtml = cleanupBlueHighlights(currentHtml);
      if (cleanedHtml !== currentHtml) {
        editor.commands.setContent(cleanedHtml);
        onChange(cleanedHtml === '<p></p>' ? '' : cleanedHtml);
      }
    }
  }, [editor, onChange]);

  // Auto-link URLs and emails function
  const handleAutoLink = useCallback(() => {
    if (editor) {
      const currentHtml = editor.getHTML();
      const linkedHtml = autoLinkContent(currentHtml);
      if (linkedHtml !== currentHtml) {
        editor.commands.setContent(linkedHtml);
        onChange(linkedHtml === '<p></p>' ? '' : linkedHtml);
      }
    }
  }, [editor, onChange]);

  // Insert template at cursor position
  const handleInsertTemplate = useCallback((template) => {
    if (editor) {
      editor.chain().focus().insertContent(template.content).run();
    }
  }, [editor]);

  // Update editor content when value changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  const characterCount = showCharacterCount && editor ? editor.storage.characterCount.characters() : undefined;

  return (
    <div className="border rounded-lg overflow-hidden bg-background">
      <EditorToolbar
        editor={editor}
        onCleanupHighlights={handleCleanupHighlights}
        onAutoLink={handleAutoLink}
        onInsertTemplate={handleInsertTemplate}
        characterCount={characterCount}
      />
      <EditorContent editor={editor} />
    </div>
  );
}
